import React, { useEffect, useState, useCallback, useRef } from "react";
import { useData } from "../../providers/DataProvider";
import {
  BonusAmountsHistory,
  BuilderInfo,
  JobDetailsProps,
} from "../../providers/types";
import httpClient from "../../utils/httpClient";
import { useMsal } from "@azure/msal-react";
import {
  TrashIcon,
  PlusIcon,
  ChevronDownIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { CurrencyDollarIcon } from "@heroicons/react/24/solid";
import {
  Modal,
  Box,
  Tooltip,
  Chip,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import { BonusStatusTimeline } from "../BonusStatusTimeline/BonusStatusTimeline";
import { BuilderTimeline } from "../BuilderTimeline/BuilderTimeline";
import { SwapHoriz, PieChart } from "@mui/icons-material";
import { AlertDialog } from "../../components/AlertDialog";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type BuilderSplitRow = BuilderInfo & {
  deductionComment?: string;
  deductionConfirmed?: boolean;
};

interface UnassignedBonusDialogProps {
  open: boolean;
  initialComment?: string;
  initialBuilderRef?: string;
  onCancel: () => void;
  onContinue: (justification: string, builderRef: string) => void;
}

interface DeductionDialogProps {
  open: boolean;
  initialValue?: string;
  builderName?: string;
  employeeNumber?: number;
  builderEmail?: string;
  onCancel: () => void;
  onContinue: (justification: string) => void;
}

interface BuilderSplitModalProps {
  jobId: number;
}

// ---------------------------------------------------------------------------
// UnassignedBonusDialog
// ---------------------------------------------------------------------------
const UnassignedBonusDialog: React.FC<UnassignedBonusDialogProps> = ({
  open,
  initialComment = "",
  initialBuilderRef = "",
  onCancel,
  onContinue,
}) => {
  const [justification, setJustification] = useState("");
  const [builderRef, setBuilderRef] = useState("");
  const continueButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      setJustification(initialComment);
      setBuilderRef(initialBuilderRef);
    }
  }, [open, initialComment, initialBuilderRef]);

  const isValid = justification.trim().length >= 3;

  return (
    <Modal open={open} onClose={onCancel} aria-labelledby="unassigned-bonus-title">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "95%",
          maxWidth: "620px",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: "10px",
          outline: "none",
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (isValid) onContinue(justification.trim(), builderRef.trim());
          }}
        >
          <Typography id="unassigned-bonus-title" variant="h6" sx={{ mb: 2 }}>
            Add Unassigned Bonus: Explanation Required
          </Typography>

          <Typography sx={{ mb: 3, color: "text.primary" }}>
            Please enter the reason this portion of the bonus is being recorded as
            Unassigned Bonus to ensure smooth processing by Ops and Payroll and to
            prevent the request from being flagged or rejected, which could delay
            payment to the builders.
          </Typography>

          <TextField
            fullWidth
            autoFocus
            multiline
            minRows={3}
            label="Enter reason / explanation"
            value={justification}
            onChange={(e) => setJustification(e.target.value.slice(0, 500))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                continueButtonRef.current?.focus();
              }
            }}
            error={justification.length > 0 && !isValid}
            helperText={
              justification.length > 0 && !isValid
                ? "Please enter an explanation to continue (minimum 3 characters)."
                : " "
            }
            inputProps={{ maxLength: 500 }}
            sx={{ mb: 2 }}
          />
          <div
            className={`text-right text-xs -mt-2 mb-2 ${justification.length >= 450 ? "text-red-500" : "text-gray-400"
              }`}
          >
            {500 - justification.length} characters remaining
          </div>

          <TextField
            fullWidth
            label="Employee / Ex-Employee name or details (optional)"
            value={builderRef}
            onChange={(e) => setBuilderRef(e.target.value.slice(0, 50))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                continueButtonRef.current?.focus();
              }
            }}
            inputProps={{ maxLength: 50 }}
            helperText=" "
          />
          <div
            className={`text-right text-xs -mt-3 mb-2 ${builderRef.length >= 45 ? "text-red-500" : "text-gray-400"
              }`}
          >
            {50 - builderRef.length} characters remaining
          </div>

          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" onClick={onCancel} type="button">
              Cancel
            </Button>
            <Button ref={continueButtonRef} variant="contained" disabled={!isValid} type="submit">
              Continue
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// DeductionDialog
// ---------------------------------------------------------------------------
const DeductionDialog: React.FC<DeductionDialogProps> = ({
  open,
  initialValue = "",
  builderName,
  employeeNumber,
  builderEmail,
  onCancel,
  onContinue,
}) => {
  const [justification, setJustification] = useState("");
  const continueButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) setJustification(initialValue || "");
  }, [open, initialValue]);

  const isValid = justification.trim().length >= 3;

  return (
    <Modal
      open={open}
      onClose={() => onCancel()}
      aria-labelledby="deduction-dialog-title"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "95%",
          maxWidth: "620px",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: "10px",
          outline: "none",
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (isValid) onContinue(justification.trim());
          }}
        >
          <Typography id="deduction-dialog-title" variant="h6" sx={{ mb: 2 }}>
            Bonus Deduction: Explanation Required
          </Typography>

          {/* ── Builder info panel ── */}
          {(builderName || employeeNumber || builderEmail) && (
            <Box
              sx={{
                mb: 2.5,
                px: 2,
                py: 1.5,
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              {builderName && (
                <div>
                  <div
                    style={{
                      fontSize: "0.68rem",
                      fontWeight: 600,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Builder
                  </div>
                  <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1e293b" }}>
                    {builderName}
                  </div>
                </div>
              )}
              {employeeNumber ? (
                <div>
                  <div
                    style={{
                      fontSize: "0.68rem",
                      fontWeight: 600,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Emp ID
                  </div>
                  <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "#1e293b" }}>
                    {employeeNumber}
                  </div>
                </div>
              ) : null}
              {builderEmail && (
                <div>
                  <div
                    style={{
                      fontSize: "0.68rem",
                      fontWeight: 600,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Email
                  </div>
                  <div style={{ fontSize: "0.875rem", color: "#1e293b" }}>{builderEmail}</div>
                </div>
              )}
            </Box>
          )}

          <Typography sx={{ mb: 3, color: "text.primary" }}>
            Please clearly explain the reason for the bonus deduction and include any supporting
            details to ensure smooth processing by Ops and Payroll and to prevent the request from
            being flagged or rejected, which could delay payment to the builders.
          </Typography>

          <TextField
            fullWidth
            autoFocus
            multiline
            minRows={3}
            label="Enter reason / explanation"
            value={justification}
            onChange={(e) => setJustification(e.target.value.slice(0, 500))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                continueButtonRef.current?.focus();
              }
            }}
            error={justification.length > 0 && !isValid}
            helperText={
              justification.length > 0 && !isValid
                ? "Please enter an explanation to continue (minimum 3 characters)."
                : " "
            }
            inputProps={{ maxLength: 500 }}
          />
          <div
            className={`text-right text-xs -mt-2 mb-2 ${justification.length >= 450 ? "text-red-500" : "text-gray-400"
              }`}
          >
            {500 - justification.length} characters remaining
          </div>

          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" onClick={onCancel} type="button">
              Cancel
            </Button>
            <Button ref={continueButtonRef} variant="contained" disabled={!isValid} type="submit">
              Continue
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const UNASSIGNED_BONUS_DEFAULTS = {
  builderId: 0,
  builderNumber: 999999,
  employeeNumber: 999999,
  builderEmail: "noreply@highlandhomes.com",
  split: 0,
  splitAmount: 0,
  deductionAmount: 0,
  createdOn: "",
  createdBy: "",
  updatedOn: "",
  updatedBy: "",
  isActive: false,
  bonusTypeId: 1,
} as const;

const isUnassignedBonus = (b: BuilderInfo) =>
  b.employeeNumber === UNASSIGNED_BONUS_DEFAULTS.employeeNumber &&
  b.builderEmail === UNASSIGNED_BONUS_DEFAULTS.builderEmail;

// ---------------------------------------------------------------------------
// Shared "Unassigned Bonus" cell renderer
// ---------------------------------------------------------------------------
const UnassignedBonusCell: React.FC<{
  builderName: string;
  onEdit?: () => void;
}> = ({ builderName, onEdit }) => (
  <div className="flex items-start justify-between gap-2 rounded-md bg-blue-50 px-3 py-2 min-w-0">
    <div className="min-w-0">
      <div className="text-sm font-semibold text-blue-800">Unassigned Bonus</div>
      {builderName && builderName !== "Unassigned Bonus" && (
        <div className="text-xs text-blue-700 whitespace-normal break-words">{builderName}</div>
      )}
    </div>
    {onEdit && (
      <button
        type="button"
        onClick={onEdit}
        className="shrink-0 p-1 text-blue-500 hover:text-blue-700 rounded"
        aria-label="Edit unassigned bonus explanation"
      >
        <PencilIcon className="h-4 w-4" />
      </button>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Compact deduction badge — truncated text + tooltip + pencil/trash actions
// ---------------------------------------------------------------------------
const DEDUCTION_BADGE_MAX_CHARS = 28;

const DeductionCommentBadge: React.FC<{
  comment?: string;
  builderName?: string;
  employeeNumber?: number;
  builderEmail?: string;
  onEdit?: () => void;
  onDelete?: () => void;
}> = ({ comment, builderName, employeeNumber, builderEmail, onEdit, onDelete }) => {
  if (!comment?.trim()) return null;

  const trimmedComment = comment.trim();
  const isTruncated = trimmedComment.length > DEDUCTION_BADGE_MAX_CHARS;
  const displayComment = isTruncated
    ? `${trimmedComment.slice(0, DEDUCTION_BADGE_MAX_CHARS).trimEnd()}…`
    : trimmedComment;

  const metaParts: string[] = [];
  if (builderName) metaParts.push(builderName);
  if (employeeNumber) metaParts.push(`EmpID: ${employeeNumber}`);
  if (builderEmail) metaParts.push(builderEmail);

  const tooltipContent = (
    <div style={{ maxWidth: 320 }}>
      <div
        style={{
          fontWeight: 700,
          fontSize: "0.7rem",
          color: "#92400e",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          marginBottom: 6,
        }}
      >
        Deduction Explanation
      </div>
      <div style={{ fontSize: "0.875rem", color: "#1f2937", lineHeight: 1.5 }}>
        {trimmedComment}
      </div>
      {metaParts.length > 0 && (
        <div
          style={{
            marginTop: 6,
            fontSize: "0.78rem",
            color: "#6b7280",
            borderTop: "1px solid #e5e7eb",
            paddingTop: 6,
          }}
        >
          {metaParts.join(" | ")}
        </div>
      )}
    </div>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement="top"
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "#ffffff",
            color: "#1f2937",
            border: "1px solid #e5e7eb",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            borderRadius: "8px",
            px: 2,
            py: 1.5,
            "& .MuiTooltip-arrow": {
              color: "#ffffff",
              "&::before": {
                border: "1px solid #e5e7eb",
              },
            },
          },
        },
      }}
    >
      <div className="mt-1.5 inline-flex w-full items-center justify-between gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs cursor-default">
        <div className="min-w-0 flex items-baseline gap-1.5">
          <span className="shrink-0 font-semibold text-amber-700">Deduction Explanation:</span>
          <span className="truncate text-gray-800">{displayComment}</span>
        </div>

        {(onEdit || onDelete) && (
          <div className="flex shrink-0 items-center gap-0.5 ml-1">
            {onEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                className="p-0.5 text-amber-500 hover:text-amber-700 rounded"
                aria-label="Edit deduction explanation"
              >
                <PencilIcon className="h-3.5 w-3.5" />
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                className="p-0.5 text-red-400 hover:text-red-600 rounded"
                aria-label="Remove deduction explanation"
              >
                <TrashIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// BuilderSplitModal
// ---------------------------------------------------------------------------
const BuilderSplitModal: React.FC<BuilderSplitModalProps> = ({ jobId }) => {
  const { role } = useData();
  const { accounts } = useMsal();

  const [open, setOpen] = useState<boolean>(false);
  const [builderSplits, setBuilderSplits] = useState<BuilderSplitRow[]>([]);
  const [job, setJob] = useState<JobDetailsProps | undefined>(undefined);

  const [allBuilders, setAllBuilders] = useState<
    {
      builderId: number;
      builderEmail: string;
      builderName: string;
      builderNumber: string;
      employeeNumber: number;
    }[]
  >([]);

  const [comments, setComments] = useState<string>("");
  const [isReadOnly, setIsReadOnly] = useState<boolean>(true);

  const [isHistoryExpanded, setIsHistoryExpanded] = useState<boolean>(false);
  const [bonusType, setBonusType] = useState<string>(
    localStorage.getItem("cba.bonusType") || "ClosingBonus"
  );

  const [hasUnassigned, setHasUnassigned] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [isAssigning, setIsAssigning] = useState(false);

  // Activity & Audit Trail — event counts
  const [activeHistoryTab, setActiveHistoryTab] = useState<string>("All");
  const [allEventCount, setAllEventCount] = useState<number>(0);
  const [commentsEventCount, setCommentsEventCount] = useState<number>(0);
  const [bonusStatusEventCount, setBonusStatusEventCount] = useState<number>(0);
  const [builderSplitEventCount, setBuilderSplitEventCount] = useState<number>(0);
  const [bonusAmountsEventCount, setBonusAmountsEventCount] = useState<number>(0);
  const [payrollEventCount, setPayrollEventCount] = useState<number>(0);

  // Unassigned Bonus dialog state
  const [unassignedDialogOpen, setUnassignedDialogOpen] = useState(false);
  const [unassignedBonusComment, setUnassignedBonusComment] = useState<string>("");
  const [unassignedBonusBuilderRef, setUnassignedBonusBuilderRef] = useState<string>("");

  // Per-row deduction dialog state
  const [deductionDialogOpen, setDeductionDialogOpen] = useState(false);
  const [pendingDeductionIndex, setPendingDeductionIndex] = useState<number | null>(null);
  const [deductionDialogInitialValue, setDeductionDialogInitialValue] = useState<string>("");
  const [pendingDeductionBuilder, setPendingDeductionBuilder] = useState<{
    builderName?: string;
    employeeNumber?: number;
    builderEmail?: string;
  }>({});
  const ignoreNextDeductionFocusRef = useRef(false);

  // ---------------------------------------------------------------------------
  // Modal open / close
  // ---------------------------------------------------------------------------
  const resetJustificationState = () => {
    setPendingDeductionIndex(null);
    setDeductionDialogInitialValue("");
    setUnassignedBonusComment("");
    setUnassignedBonusBuilderRef("");
    setPendingDeductionBuilder({});
  };

  const handleOpen = () => {
    resetJustificationState();
    setOpen(true);
  };

  const handleClose = () => {
    resetJustificationState();
    setOpen(false);
    setBuilderSplits([]);
    setHasUnassigned(false);
    setShowPreview(false);
    setComments("");
  };

  // ---------------------------------------------------------------------------
  // Alert Dialog state
  // ---------------------------------------------------------------------------
  const [alertState, setAlertState] = useState<{
    open: boolean;
    title: string;
    message: string;
    severity?: "info" | "warning" | "error" | "success";
    confirmLabel?: string;
    cancelLabel?: string;
    onConfirm?: () => void;
    onCancel?: () => void;
  }>({ open: false, title: "", message: "" });

  const showAlert = useCallback(
    (
      title: string,
      message: string,
      severity: "info" | "warning" | "error" | "success" = "info",
      onConfirm?: () => void
    ) => {
      setAlertState({
        open: true,
        title,
        message,
        severity,
        onConfirm: () => {
          setAlertState((s) => ({ ...s, open: false }));
          onConfirm?.();
        },
      });
    },
    []
  );

  const showConfirm = useCallback(
    (
      title: string,
      message: string,
      severity: "info" | "warning" | "error" | "success" = "warning",
      onConfirm?: () => void,
      onCancel?: () => void,
      confirmLabel = "Confirm",
      cancelLabel = "Cancel"
    ) => {
      setAlertState({
        open: true,
        title,
        message,
        severity,
        confirmLabel,
        cancelLabel,
        onConfirm: () => {
          setAlertState((s) => ({ ...s, open: false }));
          onConfirm?.();
        },
        onCancel: () => {
          setAlertState((s) => ({ ...s, open: false }));
          onCancel?.();
        },
      });
    },
    []
  );

  // ---------------------------------------------------------------------------
  // Per-row deduction helpers
  // ---------------------------------------------------------------------------
  const openDeductionDialogForRow = useCallback(
    (index: number) => {
      const row = builderSplits[index];
      if (!row) return;
      setPendingDeductionIndex(index);
      setDeductionDialogInitialValue(row.deductionComment || "");
      setPendingDeductionBuilder({
        builderName: row.builderName,
        employeeNumber: row.employeeNumber,
        builderEmail: row.builderEmail,
      });
      setDeductionDialogOpen(true);
    },
    [builderSplits]
  );

  const handleClearDeductionJustification = useCallback((index: number) => {
    setBuilderSplits((prev) =>
      prev.map((b, i) =>
        i === index
          ? { ...b, deductionAmount: 0, deductionComment: "", deductionConfirmed: false }
          : b
      )
    );
  }, []);

  const handleEditUnassignedBonus = useCallback(() => {
    setUnassignedDialogOpen(true);
  }, []);

  // ---------------------------------------------------------------------------
  // Role / job helpers
  // ---------------------------------------------------------------------------
  const userRoles = (accounts[0]?.idTokenClaims?.roles as string[]) || [];
  const hasOpsPlusRole =
    userRoles.includes("OpsPlus") || role === "OpsPlus" || role === "Opsplus";
  const isModelHome = job?.jobTypeCode === "M";
  const currentClosingBonus = job?.bonusDetail?.bonusAmounts?.closingBonus || 0;
  const currentStage5Bonus = job?.bonusDetail?.bonusAmounts?.stage5Bonus || 0;
  const previewClosingBonus = currentClosingBonus + currentStage5Bonus;
  const previewStage5Bonus = 0;
  const showAssignButton =
    hasOpsPlusRole && isModelHome && bonusType === "ClosingBonus" && currentStage5Bonus > 0;

  // ---------------------------------------------------------------------------
  // Assign Stage5 → Closing
  // ---------------------------------------------------------------------------
  const handleAssignStage5ToClosing = async () => {
    showConfirm(
      "Assign Stage5 → Closing",
      "Are you sure you want to assign Stage5 Bonus to Closing Bonus? This action cannot be undone.",
      "warning",
      async () => {
        setIsAssigning(true);
        try {
          await httpClient.post("/api/cba/bonus/assign-stage5-to-closing", {
            JobId: jobId,
            UpdatedBy: accounts[0].username,
          });
          showAlert(
            "Assign Stage5 → Closing",
            "Stage5 Bonus has been successfully assigned to Closing Bonus.",
            "success"
          );
          handleClose();
          window.location.reload();
        } catch (error: any) {
          console.error("Failed to assign Stage5 to Closing Bonus:", error);
          showAlert(
            "Assign Stage5 → Closing",
            error.response?.data?.message ||
            "An error occurred while assigning Stage5 to Closing Bonus. Please try again.",
            "error"
          );
        } finally {
          setIsAssigning(false);
        }
      }
    );
  };

  // ---------------------------------------------------------------------------
  // Builder row operations
  // ---------------------------------------------------------------------------
  const handleAddBuilder = useCallback(() => {
    setBuilderSplits((prev) => {
      const newBuilder: BuilderSplitRow = {
        builderId: 0,
        jobId,
        builderName: "",
        builderNumber: 0,
        employeeNumber: 0,
        builderEmail: "",
        split: 0,
        splitAmount: 0,
        deductionAmount: 0,
        deductionComment: "",
        deductionConfirmed: false,
        createdOn: "",
        createdBy: "",
        updatedOn: "",
        updatedBy: "",
        isActive: false,
        bonusTypeId: 1,
      };

      const unassignedIdx = prev.findIndex(isUnassignedBonus);
      if (unassignedIdx !== -1) {
        const list = [...prev];
        list.splice(unassignedIdx, 0, newBuilder);
        return list;
      }
      return [...prev, newBuilder];
    });
  }, [jobId]);

  // Unassigned Bonus toggle ------------------------------------------------
  const handleToggleUnassignedBonus = useCallback(() => {
    if (hasUnassigned) {
      setHasUnassigned(false);
      setUnassignedBonusComment("");
      setUnassignedBonusBuilderRef("");
      setBuilderSplits((prev) => prev.filter((b) => !isUnassignedBonus(b)));
    } else {
      setUnassignedDialogOpen(true);
    }
  }, [hasUnassigned]);

  const handleUnassignedDialogContinue = useCallback(
    (justification: string, builderRef: string) => {
      const resolvedName = builderRef.length > 0 ? builderRef : "Unassigned Bonus";

      setUnassignedBonusComment(justification);
      setUnassignedBonusBuilderRef(builderRef);

      setBuilderSplits((prev) => {
        const existingIdx = prev.findIndex(isUnassignedBonus);
        if (existingIdx !== -1) {
          return prev.map((b, i) =>
            i === existingIdx ? { ...b, builderName: resolvedName } : b
          );
        }
        return [
          ...prev,
          {
            ...UNASSIGNED_BONUS_DEFAULTS,
            jobId,
            builderName: resolvedName,
            deductionComment: "",
            deductionConfirmed: false,
          },
        ];
      });

      setHasUnassigned(true);
      setUnassignedDialogOpen(false);
    },
    [jobId]
  );

  // Builder / split / amount changes ---------------------------------------
  const handleBuilderChange = useCallback(
    (index: number, employeeNumber: number) => {
      setBuilderSplits((prev) =>
        prev.map((builderSplit, idx) => {
          if (idx !== index) return builderSplit;
          const selected = allBuilders.find((b) => b.employeeNumber === employeeNumber);
          return selected
            ? {
              ...builderSplit,
              builderId: selected.builderId,
              builderName: selected.builderName,
              builderEmail: selected.builderEmail,
              builderNumber: Number(selected.builderNumber),
              employeeNumber: selected.employeeNumber,
            }
            : builderSplit;
        })
      );
    },
    [allBuilders]
  );

  const handleSplitChange = useCallback(
    (index: number, split: number) => {
      const bonusAmount =
        bonusType === "Stage5Bonus"
          ? job?.bonusDetail.bonusAmounts?.stage5Bonus || 0
          : job?.bonusDetail.bonusAmounts?.closingBonus || 0;

      setBuilderSplits((prev) =>
        prev.map((b, idx) =>
          idx === index
            ? {
              ...b,
              split: Math.max(0, split),
              splitAmount: parseFloat((bonusAmount * (split / 100)).toFixed(2)),
            }
            : b
        )
      );
    },
    [job, bonusType]
  );

  // Deduction flow ---------------------------------------------------------
  const handleDeductionFocus = useCallback(
    (index: number) => {
      if (isReadOnly) return;

      const row = builderSplits[index];
      if (!row || isUnassignedBonus(row)) return;
      if (row.deductionConfirmed) return;

      if (ignoreNextDeductionFocusRef.current) {
        ignoreNextDeductionFocusRef.current = false;
        return;
      }

      if (deductionDialogOpen) return;

      setPendingDeductionIndex(index);
      setDeductionDialogInitialValue(row.deductionComment || "");
      setPendingDeductionBuilder({
        builderName: row.builderName,
        employeeNumber: row.employeeNumber,
        builderEmail: row.builderEmail,
      });
      setDeductionDialogOpen(true);
    },
    [isReadOnly, builderSplits, deductionDialogOpen]
  );

  const handleDeductionDialogCancel = useCallback(() => {
    ignoreNextDeductionFocusRef.current = true;
    setDeductionDialogOpen(false);
    setPendingDeductionIndex(null);
    setDeductionDialogInitialValue("");
    setPendingDeductionBuilder({});
  }, []);

  const handleDeductionDialogContinue = useCallback(
    (justification: string) => {
      const idx = pendingDeductionIndex;

      if (idx !== null) {
        setBuilderSplits((prev) =>
          prev.map((b, i) =>
            i === idx ? { ...b, deductionComment: justification, deductionConfirmed: true } : b
          )
        );
      }

      setDeductionDialogOpen(false);
      setPendingDeductionIndex(null);
      setDeductionDialogInitialValue("");
      setPendingDeductionBuilder({});
      ignoreNextDeductionFocusRef.current = false;

      if (idx !== null) {
        setTimeout(() => {
          const el = document.getElementById(`deduction-${idx}`) as HTMLInputElement | null;
          el?.focus();
          el?.select?.();
        }, 0);
      }
    },
    [pendingDeductionIndex]
  );

  const handleDeductionChange = useCallback((index: number, deductionAmount: number) => {
    setBuilderSplits((prev) =>
      prev.map((b, idx) => {
        if (idx !== index) return b;
        if (!b.deductionConfirmed && deductionAmount > 0) return b;
        if (b.splitAmount < deductionAmount) return b;
        return { ...b, deductionAmount: Math.max(0, deductionAmount) };
      })
    );
  }, []);

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------
  const handleContinue = useCallback(() => {
    if (!builderSplits.length) {
      showAlert("Missing Builder", "Please select at least one builder.", "warning");
      return;
    }

    if (builderSplits.some((b) => !b.builderName)) {
      showAlert("Missing Builder", "One or more rows are missing a builder name.", "warning");
      return;
    }

    const emailSet = new Set<string>();
    const hasDuplicateEmails = builderSplits
      .filter((b) => !isUnassignedBonus(b))
      .some((b) => {
        const email = (b.builderEmail || "").toLowerCase();
        if (emailSet.has(email)) return true;
        emailSet.add(email);
        return false;
      });

    if (hasDuplicateEmails) {
      showAlert(
        "Duplicate Builders",
        "Duplicate builder names detected. Each builder must be unique.",
        "error"
      );
      return;
    }

    if (builderSplits.some((b) => b.split === 0)) {
      showAlert(
        "Invalid Split",
        "All builders must have a split percentage greater than 0%.",
        "warning"
      );
      return;
    }

    if (builderSplits.reduce((acc, b) => acc + b.split, 0) !== 100) {
      showAlert(
        "Invalid Split",
        "Total split must equal 100%. Please adjust the builder splits before saving.",
        "warning"
      );
      return;
    }

    const actualBonusAmount =
      bonusType === "Stage5Bonus"
        ? job?.bonusDetail.bonusAmounts?.stage5Bonus || 0
        : job?.bonusDetail.bonusAmounts?.closingBonus || 0;

    const totalSplitAmount = parseFloat(
      builderSplits.reduce((total, b) => total + b.splitAmount, 0).toFixed(2)
    );

    if (totalSplitAmount > actualBonusAmount) {

      const tolerance = 0.1; // Allow a small tolerance for rounding issues
      if (totalSplitAmount - actualBonusAmount < tolerance) {
        // If the total split amount exceeds the actual bonus amount by less than the tolerance,
        // adjust the last builder's split amount to compensate
        builderSplits.forEach((b, idx) => {
          if (idx === builderSplits.length - 1) {
            b.splitAmount -= totalSplitAmount - actualBonusAmount;
          }
        });
      }
      else {
        showAlert(
          "Amount Exceeded",
          `Total bonus amount cannot exceed $${actualBonusAmount.toLocaleString("en-US", {
            minimumFractionDigits: 2,
          })}.\n\nCurrent total: $${totalSplitAmount.toLocaleString("en-US", {
            minimumFractionDigits: 2,
          })}`,
          "error"
        );
        return;
      }
    }

    const rowsMissingDeductionComment = builderSplits.filter(
      (b) =>
        (b.deductionAmount || 0) > 0 &&
        !b.deductionConfirmed &&
        (b.deductionComment?.trim()?.length ?? 0) < 3
    );

    if (rowsMissingDeductionComment.length > 0) {
      showAlert(
        "Missing Deduction Explanation",
        "Each builder with a deduction amount must have an individual deduction explanation.",
        "warning"
      );
      return;
    }

    const deductionSummary = builderSplits
      .filter((b) => (b.deductionAmount || 0) > 0 && b.deductionComment?.trim())
      .map(
        (b) =>
          `|Deduction Explanation| Builder: ${b.builderName} | EmpID: ${b.employeeNumber
          } | Amount: ${(b.deductionAmount || 0).toFixed(2)} | Comment: ${b.deductionComment?.trim()}`
      )
      .join(" ");

    let additionalComments = "";
    if (deductionSummary) additionalComments += deductionSummary;
    if (unassignedBonusComment.trim() !== "") {
      additionalComments += `${additionalComments ? " " : ""}|Unassigned Bonus Explanation: ${unassignedBonusComment.trim()} |Unassigned Bonus Reference: ${unassignedBonusBuilderRef.trim() || "N/A"
        }`;
    }

    const effectiveComments = comments.trim()
      ? `${comments}${additionalComments ? ` ${additionalComments}` : ""}`
      : additionalComments.trim();

    if (!effectiveComments) {
      showAlert("Comments Required", "Please enter comments before saving.", "warning");
      return;
    }

    httpClient.post("/api/cba/builders/bulk", {
      JobId: jobId,
      UpdatedBy: accounts[0].username,
      Comments: effectiveComments,
      BulkUpdateBuildersRequests: builderSplits.map((builder) => ({
        JobId: jobId,
        BuilderId: builder.builderId,
        BuilderName: builder.builderName,
        BuilderEmail: builder.builderEmail,
        EmployeeNumber: builder.employeeNumber,
        Split: builder.split,
        SplitAmount: builder.splitAmount.toFixed(2),
        DeductionAmount: builder.deductionAmount.toFixed(2) || 0,
        // DeductionComment: builder.deductionComment?.trim() || "",
        CreatedBy: accounts[0].username,
      })),
    })
      .then(() => {
        handleClose();
        window.location.reload();
      })
      .catch((error) => {
        console.error("Failed to save builder splits:", error);
        showAlert("Save Failed", "An error occurred while saving. Please try again.", "error");
      });
  }, [
    accounts,
    bonusType,
    builderSplits,
    comments,
    handleClose,
    job,
    jobId,
    showAlert,
    unassignedBonusBuilderRef,
    unassignedBonusComment,
  ]);

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------
  const loadAllBuilders = useCallback(() => {
    httpClient.post(`/api/cba/builders/search`)
      .then((response) => setAllBuilders(response.data))
      .catch((error) => console.error("Error fetching job builder details:", error));
  }, []);

  const loadJob = useCallback(() => {
    httpClient.get(`/api/cba/job-details/${jobId}`, {
      headers: { roleType: role },
    })
      .then((response) => {
        setJob(response.data);
        setIsReadOnly(response.data.bonusDetail.isReadOnly);
        setComments(response.data.bonusDetail?.comments || "");

        if (!response.data.bonusDetail.isReadOnly) loadAllBuilders();

        let builders: BuilderSplitRow[] = [];

        if (response.data.isDefaultSplitAmount) {
          builders = response.data.builders.map((builder: any) => {
            const closingAmount = response.data.bonusDetail?.bonusAmounts?.closingBonus || 0;
            const stage5Amount = response.data.bonusDetail?.bonusAmounts?.stage5Bonus || 0;
            return {
              ...builder,
              splitAmount:
                (bonusType === "Stage5Bonus" ? stage5Amount : closingAmount) *
                builder.split *
                0.01,
              deductionComment: builder.deductionComment || "",
              deductionConfirmed:
                (builder.deductionAmount || 0) > 0 || !!builder.deductionComment,
            };
          });
        } else {
          builders = response.data.builders.map((builder: any) => ({
            ...builder,
            deductionComment: builder.deductionComment || "",
            deductionConfirmed:
              (builder.deductionAmount || 0) > 0 || !!builder.deductionComment,
          }));
        }

        setBuilderSplits(builders);
        setHasUnassigned(builders.some(isUnassignedBonus));
      })
      .catch((error) => console.error("Error fetching job details:", error));
  }, [jobId, loadAllBuilders, role, bonusType]);

  useEffect(() => {
    if (open) {
      loadJob();
      loadAllBuilders();
    }
  }, [open, loadJob, loadAllBuilders]);

  useEffect(() => {
    if (job) {
      const bonusAmount =
        bonusType === "Stage5Bonus"
          ? job?.bonusDetail.bonusAmounts?.stage5Bonus || 0
          : job?.bonusDetail.bonusAmounts?.closingBonus || 0;
      setBuilderSplits((prev) =>
        prev.map((b) => ({
          ...b,
          splitAmount: parseFloat((bonusAmount * (b.split / 100)).toFixed(2)),
        }))
      );
    }
  }, [job, bonusType]);

  useEffect(() => {
    if (builderSplits.length > 0 && builderSplits.every((b) => b.splitAmount === 0)) {
      const bonusAmount =
        bonusType === "Stage5Bonus"
          ? job?.bonusDetail.bonusAmounts?.stage5Bonus || 0
          : job?.bonusDetail.bonusAmounts?.closingBonus || 0;
      setBuilderSplits((prev) =>
        prev.map((b) => ({
          ...b,
          splitAmount: parseFloat((bonusAmount * (b.split / 100)).toFixed(2)),
        }))
      );
    }
  }, [builderSplits, job, bonusType]);

  const formatMoney = (n: number) =>
    n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ---------------------------------------------------------------------------
  // JSX
  // ---------------------------------------------------------------------------
  return (
    <>
      <UnassignedBonusDialog
        open={unassignedDialogOpen}
        initialComment={unassignedBonusComment}
        initialBuilderRef={unassignedBonusBuilderRef}
        onCancel={() => setUnassignedDialogOpen(false)}
        onContinue={handleUnassignedDialogContinue}
      />

      <DeductionDialog
        open={deductionDialogOpen}
        initialValue={deductionDialogInitialValue}
        builderName={pendingDeductionBuilder.builderName}
        employeeNumber={pendingDeductionBuilder.employeeNumber}
        builderEmail={pendingDeductionBuilder.builderEmail}
        onCancel={handleDeductionDialogCancel}
        onContinue={handleDeductionDialogContinue}
      />

      <AlertDialog
        open={alertState.open}
        title={alertState.title}
        message={alertState.message}
        severity={alertState.severity}
        confirmLabel={alertState.confirmLabel ?? "OK"}
        cancelLabel={alertState.cancelLabel}
        onConfirm={alertState.onConfirm ?? (() => setAlertState((s) => ({ ...s, open: false })))}
        onCancel={alertState.onCancel}
      />

      <Tooltip title="Builder Split">
        <button onClick={handleOpen} className="p-2 text-gray-400 hover:text-gray-500">
          <CurrencyDollarIcon className="h-5 w-5 text-primary-500" />
        </button>
      </Tooltip>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "95%",
            maxWidth: "1400px",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: { xs: 2, sm: 3, md: 4 },
            maxHeight: "90vh",
            overflowY: "auto",
            borderRadius: "10px",
          }}
        >
          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
              {/* ---- Header ---- */}
              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                  Builder Split -{" "}
                  {bonusType === "Stage5Bonus" ? "Stage 5 Bonus" : "Closing Bonus"}
                  <span style={{ color: "#fff" }}>
                    {" "}
                    - {builderSplits[0]?.jobId ?? jobId ?? ""}
                  </span>
                </h3>

                {showAssignButton && (
                  <button
                    type="button"
                    onClick={() => setShowPreview(!showPreview)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    {showPreview
                      ? "Hide Preview"
                      : "Show Preview: Assign Stage5 → Closing Bonus"}
                  </button>
                )}
              </div>

              {/* ---- Assign Stage5 preview panel ---- */}
              {showAssignButton && showPreview && (
                <div className="mt-3 mb-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">
                    Preview of Changes:
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Current Closing Bonus:</span>
                      <span className="font-medium">
                        $
                        {currentClosingBonus.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Current Stage5 Bonus:</span>
                      <span className="font-medium">
                        $
                        {currentStage5Bonus.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                    <div className="border-t border-blue-300 pt-2 mt-2">
                      <div className="flex justify-between text-green-700 font-semibold">
                        <span>New Closing Bonus:</span>
                        <span>
                          $
                          {previewClosingBonus.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                      <div className="flex justify-between text-red-700 font-semibold">
                        <span>New Stage5 Bonus:</span>
                        <span>
                          $
                          {previewStage5Bonus.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleAssignStage5ToClosing}
                    disabled={isAssigning}
                    className="mt-3 w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAssigning ? "Assigning..." : "Assign Stage5 → Closing Bonus"}
                  </button>
                </div>
              )}

              {/* ---------------------------------------------------------------- */}
              {/* Builder table                                                      */}
              {/* ---------------------------------------------------------------- */}
              <div className="mt-2">
                {/* MOBILE (<= md): Card layout */}
                <div className="md:hidden space-y-3">
                  {builderSplits.map((builderSplit, index) => {
                    const totalBonus =
                      builderSplit.splitAmount - (builderSplit.deductionAmount || 0);
                    const isUnassigned = isUnassignedBonus(builderSplit);

                    return (
                      <div
                        key={index}
                        className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="w-full">
                            {isUnassigned ? (
                              <UnassignedBonusCell
                                builderName={builderSplit.builderName}
                                onEdit={!isReadOnly ? handleEditUnassignedBonus : undefined}
                              />
                            ) : (
                              <>
                                <div className="text-sm font-semibold text-gray-900 truncate">
                                  {builderSplit.builderName}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  {builderSplit.builderEmail}
                                </div>
                              </>
                            )}
                          </div>

                          {!isReadOnly && !isUnassigned && (
                            <button
                              onClick={() =>
                                setBuilderSplits((prev) =>
                                  prev.filter((_, idx) => idx !== index)
                                )
                              }
                              className="shrink-0 inline-flex items-center justify-center rounded-md border border-gray-200 p-2 text-red-600 hover:bg-red-50"
                              aria-label="Remove builder"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>

                        <div className="mt-3 grid grid-cols-2 gap-3">
                          {!isReadOnly && !isUnassigned && (
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-600">
                                Builder
                              </label>
                              <select
                                value={builderSplit.employeeNumber}
                                onChange={(e) =>
                                  handleBuilderChange(index, Number(e.target.value))
                                }
                                className="mt-1 block w-full rounded-md border-gray-300 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                              >
                                <option value="">Select Builder</option>
                                {builderSplit.builderName &&
                                  !allBuilders.some(
                                    (b) => b.employeeNumber === builderSplit.employeeNumber
                                  ) && (
                                    <option value={builderSplit.employeeNumber}>
                                      {`${builderSplit.builderName} (${builderSplit.builderEmail})`}
                                    </option>
                                  )}
                                {allBuilders.map((builder) => (
                                  <option
                                    key={`${builder.builderEmail}-${builder.builderId}`}
                                    value={builder.employeeNumber}
                                  >
                                    {`${builder.builderName} (${builder.builderEmail})`}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          <div>
                            <label className="block text-xs font-medium text-gray-600">
                              Split (%)
                            </label>
                            <input
                              type="number"
                              value={builderSplit.split}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) =>
                                handleSplitChange(
                                  index,
                                  Math.max(0, parseInt(e.target.value, 10))
                                )
                              }
                              min={0}
                              className={`mt-1 block w-full rounded-md border-gray-300 py-2 text-sm focus:border-primary-500 focus:ring-primary-500 ${isReadOnly ? "bg-gray-50 text-gray-500" : "bg-white text-gray-900"
                                }`}
                              readOnly={isReadOnly}
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600">
                              Bonus Amount
                            </label>
                            <input
                              type="number"
                              value={builderSplit.splitAmount.toFixed(2)}
                              className="mt-1 block w-full rounded-md border-gray-200 bg-gray-50 py-2 text-sm text-gray-500"
                              readOnly
                            />
                          </div>

                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-600">
                              Deductions
                            </label>
                            <input
                              id={`deduction-${index}`}
                              type="number"
                              value={builderSplit.deductionAmount || 0}
                              onFocus={(e) => {
                                handleDeductionFocus(index);
                                e.target.select();
                              }}
                              onChange={(e) =>
                                handleDeductionChange(
                                  index,
                                  Number.isFinite(parseFloat(e.target.value))
                                    ? parseFloat(e.target.value)
                                    : 0
                                )
                              }
                              className={`mt-1 block w-full rounded-md border-gray-300 py-2 text-sm focus:border-primary-500 focus:ring-primary-500 ${isReadOnly || isUnassigned
                                  ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                                  : "bg-white text-gray-900"
                                }`}
                              readOnly={
                                isReadOnly || !builderSplit.deductionConfirmed || isUnassigned
                              }
                            />
                            <DeductionCommentBadge
                              comment={builderSplit.deductionComment}
                              builderName={builderSplit.builderName}
                              employeeNumber={builderSplit.employeeNumber}
                              builderEmail={builderSplit.builderEmail}
                              onEdit={
                                !isReadOnly && !isUnassigned
                                  ? () => openDeductionDialogForRow(index)
                                  : undefined
                              }
                              onDelete={
                                !isReadOnly && !isUnassigned
                                  ? () => handleClearDeductionJustification(index)
                                  : undefined
                              }
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600">
                              Total Bonus
                            </label>
                            <div className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900">
                              ${formatMoney(totalBonus)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* DESKTOP (md+): Table layout */}
                <div className="hidden md:block">
                  <div className="overflow-x-auto -mx-2 md:mx-0">
                    <div className="min-w-[1100px] px-2 md:px-0">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="sticky top-0 bg-gray-50 z-10">
                          <tr>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[28%]">
                              Builder
                            </th>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[12%]">
                              Split (%)
                            </th>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">
                              Bonus Amount
                            </th>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[24%]">
                              Deductions
                            </th>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">
                              Total Bonus
                            </th>
                            {!isReadOnly && (
                              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%]">
                                Actions
                              </th>
                            )}
                          </tr>
                        </thead>

                        <tbody className="bg-white divide-y divide-gray-200">
                          {builderSplits.map((builderSplit, index) => {
                            const isUnassigned = isUnassignedBonus(builderSplit);

                            return (
                              <tr key={index}>
                                {/* Builder cell */}
                                <td className="px-4 lg:px-6 py-4 align-top">
                                  {isReadOnly || isUnassigned ? (
                                    isUnassigned ? (
                                      <UnassignedBonusCell
                                        builderName={builderSplit.builderName}
                                        onEdit={
                                          !isReadOnly ? handleEditUnassignedBonus : undefined
                                        }
                                      />
                                    ) : (
                                      <div className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 sm:text-sm rounded-md">
                                        <div className="text-sm font-medium text-gray-900">
                                          {builderSplit.builderName}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                          {builderSplit.builderEmail}
                                        </div>
                                      </div>
                                    )
                                  ) : (
                                    <select
                                      value={builderSplit.employeeNumber}
                                      onChange={(e) =>
                                        handleBuilderChange(index, Number(e.target.value))
                                      }
                                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                                    >
                                      <option value="">Select Builder</option>
                                      {builderSplit.builderName &&
                                        !allBuilders.some(
                                          (b) =>
                                            b.employeeNumber === builderSplit.employeeNumber
                                        ) && (
                                          <option value={builderSplit.employeeNumber}>
                                            {`${builderSplit.builderName} (${builderSplit.builderEmail})`}
                                          </option>
                                        )}
                                      {allBuilders.map((builder) => (
                                        <option
                                          key={`${builder.builderEmail}-${builder.builderId}`}
                                          value={builder.employeeNumber}
                                        >
                                          {`${builder.builderName} (${builder.builderEmail})`}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                </td>

                                {/* Split % */}
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap align-top">
                                  <input
                                    type="number"
                                    value={builderSplit.split}
                                    onFocus={(e) => e.target.select()}
                                    onChange={(e) =>
                                      handleSplitChange(
                                        index,
                                        Math.max(0, parseInt(e.target.value, 10))
                                      )
                                    }
                                    min="0"
                                    className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md ${isReadOnly
                                        ? "bg-gray-50 text-gray-500"
                                        : "bg-white text-gray-900"
                                      }`}
                                    readOnly={isReadOnly}
                                  />
                                </td>

                                {/* Bonus Amount */}
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap align-top">
                                  <input
                                    type="number"
                                    value={builderSplit.splitAmount.toFixed(2)}
                                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 sm:text-sm rounded-md bg-gray-50 text-gray-500"
                                    readOnly
                                  />
                                </td>

                                {/* Deductions */}
                                <td className="px-4 lg:px-6 py-4 align-top">
                                  <input
                                    id={`deduction-${index}`}
                                    type="number"
                                    value={builderSplit.deductionAmount || 0}
                                    onFocus={(e) => {
                                      handleDeductionFocus(index);
                                      e.target.select();
                                    }}
                                    onChange={(e) =>
                                      handleDeductionChange(
                                        index,
                                        Number.isFinite(parseFloat(e.target.value))
                                          ? parseFloat(e.target.value)
                                          : 0
                                      )
                                    }
                                    className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md ${isReadOnly || isUnassigned
                                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                        : "bg-white text-gray-900"
                                      }`}
                                    readOnly={
                                      isReadOnly ||
                                      !builderSplit.deductionConfirmed ||
                                      isUnassigned
                                    }
                                  />
                                  <DeductionCommentBadge
                                    comment={builderSplit.deductionComment}
                                    builderName={builderSplit.builderName}
                                    employeeNumber={builderSplit.employeeNumber}
                                    builderEmail={builderSplit.builderEmail}
                                    onEdit={
                                      !isReadOnly && !isUnassigned
                                        ? () => openDeductionDialogForRow(index)
                                        : undefined
                                    }
                                    onDelete={
                                      !isReadOnly && !isUnassigned
                                        ? () => handleClearDeductionJustification(index)
                                        : undefined
                                    }
                                  />
                                </td>

                                {/* Total Bonus */}
                                <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                                  $
                                  {formatMoney(
                                    builderSplit.splitAmount - (builderSplit.deductionAmount || 0)
                                  )}
                                </td>

                                {/* Actions */}
                                {!isReadOnly && (
                                  <td className="px-4 lg:px-6 py-4 whitespace-nowrap align-top">
                                    {!isUnassigned && (
                                      <button
                                        onClick={() =>
                                          setBuilderSplits((prev) =>
                                            prev.filter((_, idx) => idx !== index)
                                          )
                                        }
                                        className="text-red-600 hover:text-red-900"
                                      >
                                        <TrashIcon className="h-5 w-5" />
                                      </button>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {!isReadOnly && (
                  <div className="my-4 flex flex-col sm:flex-row gap-3 flex-wrap">
                    <button
                      onClick={handleAddBuilder}
                      className="inline-flex w-full sm:w-auto items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                      Add Builder
                    </button>

                    <button
                      onClick={handleToggleUnassignedBonus}
                      className="inline-flex w-full sm:w-auto items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                      {hasUnassigned ? "Remove Unassigned Bonus" : "Add Unassigned Bonus"}
                    </button>
                  </div>
                )}
              </div>

              <div className="flex flex-col md:grid md:grid-cols-3 gap-4">
                <div className="md:col-span-1">
                  <div className="space-y-2">
                    {bonusType === "ClosingBonus" && (
                      <div className="flex items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700">
                          Closing Bonus
                        </label>
                        <span className="text-sm text-gray-500">
                          {job?.bonusDetail?.bonusAmounts?.closingBonus?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700">
                        Stage 5 Bonus
                      </label>
                      <span className="text-sm text-gray-500">
                        {job?.bonusDetail.bonusAmounts?.stage5Bonus?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </span>
                    </div>

                    {unassignedBonusComment && unassignedBonusComment.trim() !== "" && (
                      <div className="mt-2 rounded-md bg-blue-50 border border-gray-300 p-2">
                        <p className="text-xs font-medium text-blue-800 text-left">
                          Unassigned Bonus Explanation
                        </p>
                        <p className="text-xs text-black mt-0.5 text-left">
                          {unassignedBonusComment}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="comments" className="block text-sm font-medium text-gray-700">
                    Comments
                  </label>
                  <textarea
                    id="comments"
                    rows={6}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>

              {/* ---------------------------------------------------------------- */}
              {/* Activity & Audit Trail                                             */}
              {/* ---------------------------------------------------------------- */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <button
                  type="button"
                  className="w-full flex justify-between items-center text-left hover:bg-gray-50 p-3 rounded-lg transition-colors"
                  onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                >
                  <div className="flex flex-col flex-1">
                    <span className="font-medium text-gray-900 text-base mb-2">
                      Activity &amp; Audit Trail
                    </span>

                    {!isHistoryExpanded && (
                      <div className="flex gap-2 flex-wrap">
                        <Chip
                          icon={<SwapHoriz fontSize="small" />}
                          label={`Status${bonusStatusEventCount > 0 ? ` (${bonusStatusEventCount})` : ""
                            }`}
                          size="small"
                          variant="outlined"
                          color="success"
                        />
                        <Chip
                          icon={<PieChart fontSize="small" />}
                          label={`Splits${builderSplitEventCount > 0 ? ` (${builderSplitEventCount})` : ""
                            }`}
                          size="small"
                          variant="outlined"
                          color="warning"
                        />
                      </div>
                    )}
                  </div>

                  <ChevronDownIcon
                    className={`h-5 w-5 transform transition-transform ${isHistoryExpanded ? "rotate-180" : ""
                      }`}
                  />
                </button>

                {isHistoryExpanded && (
                  <div className="mt-4">
                    <div className="border-b border-gray-200">
                      <nav className="-mb-px flex space-x-6" aria-label="History Tabs">
                        <button
                          type="button"
                          onClick={() => setActiveHistoryTab("Status")}
                          className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${activeHistoryTab === "Status"
                              ? "border-green-500 text-green-600"
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                        >
                          <SwapHoriz fontSize="small" />
                          Status
                          {bonusStatusEventCount > 0 && (
                            <Chip
                              label={bonusStatusEventCount}
                              size="small"
                              color="success"
                            />
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={() => setActiveHistoryTab("Splits")}
                          className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${activeHistoryTab === "Splits"
                              ? "border-orange-500 text-orange-600"
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                        >
                          <PieChart fontSize="small" />
                          Splits
                          {builderSplitEventCount > 0 && (
                            <Chip
                              label={builderSplitEventCount}
                              size="small"
                              color="warning"
                            />
                          )}
                        </button>
                      </nav>
                    </div>

                    <div className="mt-4 space-y-6">
                      {activeHistoryTab === "All" && (
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <p className="text-sm text-gray-500">All Activity</p>
                        </div>
                      )}

                      {activeHistoryTab === "Comments" && (
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <p className="text-sm text-gray-500">Comments</p>
                        </div>
                      )}

                      {activeHistoryTab === "Status" && (
                        <BonusStatusTimeline
                          jobId={jobId}
                          onEventCountChange={setBonusStatusEventCount}
                        />
                      )}

                      {activeHistoryTab === "Splits" && (
                        <BuilderTimeline
                          jobId={jobId}
                          onEventCountChange={setBuilderSplitEventCount}
                        />
                      )}

                      {activeHistoryTab === "Amounts" && (
                        <div className="space-y-4">
                          {job?.bonusDetail?.bonusAmounts && (
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Current Bonus Amounts
                              </h3>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-sm text-gray-500">Closing Bonus</p>
                                  <p className="text-lg font-medium text-gray-900">
                                    ${job.bonusDetail.bonusAmounts.closingBonus?.toLocaleString()}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Stage 5 Bonus</p>
                                  <p className="text-lg font-medium text-gray-900">
                                    ${job.bonusDetail.bonusAmounts.stage5Bonus?.toLocaleString()}
                                  </p>
                                </div>
                                {job.bonusDetail.bonusAmounts.comments && (
                                  <div className="col-span-2">
                                    <p className="text-sm text-gray-500">Comments</p>
                                    <p className="text-sm text-gray-900">
                                      {job.bonusDetail.bonusAmounts.comments}
                                    </p>
                                  </div>
                                )}
                                <div className="col-span-2">
                                  <p className="text-xs text-gray-400">
                                    Updated by {job.bonusDetail.bonusAmounts.updatedBy} on{" "}
                                    {job.bonusDetail.bonusAmounts.updatedOn
                                      ? new Date(
                                        job.bonusDetail.bonusAmounts.updatedOn
                                      ).toLocaleString()
                                      : "N/A"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {job?.bonusDetail?.bonusAmountsHistories &&
                            job.bonusDetail.bonusAmountsHistories.length > 0 && (
                              <div className="bg-white p-4 rounded-lg border border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                  Bonus Amounts History
                                </h3>
                                <div className="space-y-4">
                                  {job.bonusDetail.bonusAmountsHistories
                                    .sort(
                                      (a, b) =>
                                        new Date(b.updatedOn || 0).getTime() -
                                        new Date(a.updatedOn || 0).getTime()
                                    )
                                    .map((bonusAmount: BonusAmountsHistory) => (
                                      <div
                                        key={bonusAmount.bonusAmountsHistoryId}
                                        className="bg-gray-50 p-4 rounded-lg"
                                      >
                                        <div className="grid grid-cols-2 gap-4">
                                          <div>
                                            <p className="text-sm text-gray-500">Closing Bonus</p>
                                            <p className="text-lg font-medium text-gray-900">
                                              ${bonusAmount.closingBonus?.toLocaleString()}
                                            </p>
                                          </div>
                                          <div>
                                            <p className="text-sm text-gray-500">Stage 5 Bonus</p>
                                            <p className="text-lg font-medium text-gray-900">
                                              ${bonusAmount.stage5Bonus?.toLocaleString()}
                                            </p>
                                          </div>
                                          {bonusAmount.comments && (
                                            <div className="col-span-2">
                                              <p className="text-sm text-gray-500">Comments</p>
                                              <p className="text-sm text-gray-900">
                                                {bonusAmount.comments}
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-400 mt-2">
                                          Updated by {bonusAmount.updatedBy} on{" "}
                                          {bonusAmount.updatedOn
                                            ? new Date(bonusAmount.updatedOn).toLocaleString()
                                            : "N/A"}
                                        </p>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            )}
                        </div>
                      )}

                      {activeHistoryTab === "Payroll" && (
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                          <p className="text-sm text-gray-500">Payroll Data</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            {!isReadOnly && (
              <button
                onClick={handleContinue}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                disabled={isReadOnly}
              >
                Save
              </button>
            )}

            <button
              onClick={handleClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </Box>
      </Modal>
    </>
  );
};

export default BuilderSplitModal;